<template>
  <el-dialog
    :visible.sync="dialogVisible"
    :title="fileName"
    width="1100px"
    top="3vh"
    append-to-body
    destroy-on-close
    custom-class="attachment-preview-dialog"
    @close="handleClose"
  >
    <div class="apd-body">
      <div v-if="loading" class="apd-status">正在加载预览…</div>

      <!-- 图片 -->
      <div v-else-if="kind === 'image'" class="apd-image-stage" @wheel.prevent="onWheel">
        <img
          v-if="resolvedUrl"
          class="apd-image"
          :src="resolvedUrl"
          :alt="fileName"
          :style="imageStyle"
          draggable="false"
          @mousedown.prevent="onDragStart"
        />
        <div v-else class="apd-status">暂无预览地址</div>
      </div>

      <!-- 视频 -->
      <div v-else-if="kind === 'video'" class="apd-media">
        <video
          v-if="resolvedUrl"
          ref="videoPlayer"
          class="apd-video"
          :src="resolvedUrl"
          controls
          controlslist="nodownload"
        ></video>
        <div v-else class="apd-status">暂无视频地址</div>
      </div>

      <!-- 音频 -->
      <div v-else-if="kind === 'audio'" class="apd-audio">
        <div class="apd-audio-icon" aria-hidden="true">♪</div>
        <audio v-if="resolvedUrl" class="apd-audio-player" :src="resolvedUrl" controls></audio>
        <div v-else class="apd-status">暂无音频地址</div>
      </div>

      <!-- PDF -->
      <div v-else-if="kind === 'pdf'" class="apd-pdf">
        <iframe v-if="resolvedUrl" class="apd-pdf-frame" :src="resolvedUrl" title="PDF 预览"></iframe>
        <div v-else class="apd-status">暂无预览地址</div>
      </div>

      <!-- Office -->
      <div v-else-if="kind === 'office'" class="apd-pdf">
        <iframe v-if="officePreviewUrl" class="apd-pdf-frame" :src="officePreviewUrl" title="文档预览"></iframe>
        <div v-else class="apd-status">该文档暂不支持在线预览，请下载查看</div>
      </div>

      <!-- 不支持 -->
      <div v-else class="apd-fallback">
        <p>该文件暂不支持在线预览，请下载查看</p>
      </div>
    </div>

    <div slot="footer" class="apd-footer">
      <el-button v-if="kind === 'image' && !loading" size="small" @click="resetImage">重置缩放</el-button>
      <span class="apd-footer-spacer"></span>
      <el-button type="primary" size="small" :loading="downloading" @click="onDownload">
        {{ downloading ? '下载中…' : '下载' }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import axios from 'axios'

const OFFICE_EXT = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx']

function pickExt(file) {
  if (!file) return ''
  const fromField = String(file.ext || '').trim()
  if (fromField) return fromField.replace(/^\./, '').toLowerCase()
  const name = String(file.filename || file.name || '')
  const m = name.toLowerCase().match(/\.([a-z0-9]+)$/)
  return m ? m[1] : ''
}

function getKind(ext) {
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(ext)) return 'image'
  if (['mp4', 'webm', 'mov', 'm4v'].includes(ext)) return 'video'
  if (['mp3', 'wav', 'ogg'].includes(ext)) return 'audio'
  if (ext === 'pdf') return 'pdf'
  if (OFFICE_EXT.includes(ext)) return 'office'
  return 'unsupported'
}

function pickIdentifier(file, url) {
  if (file && file.identifier) return String(file.identifier)
  const matched = String(url || '').match(/[?&]identifier=([^&]+)/)
  if (!matched) return ''
  try {
    return decodeURIComponent(matched[1])
  } catch (e) {
    return matched[1]
  }
}

export default {
  name: 'AttachmentPreviewDialog',
  props: {
    visible: { type: Boolean, default: false },
    file: { type: Object, default: null }
  },
  data() {
    return {
      loading: false,
      downloading: false,
      resolvedUrl: '',
      scale: 1,
      translate: { x: 0, y: 0 },
      dragging: false,
      dragStart: { x: 0, y: 0, tx: 0, ty: 0 },
      resolveSeq: 0
    }
  },
  computed: {
    dialogVisible: {
      get() { return this.visible },
      set(val) { this.$emit('update:visible', val) }
    },
    fileName() {
      if (!this.file) return '附件预览'
      return this.file.filename || this.file.name || '附件预览'
    },
    ext() {
      return pickExt(this.file)
    },
    kind() {
      return getKind(this.ext)
    },
    imageStyle() {
      return {
        transform: `translate(${this.translate.x}px, ${this.translate.y}px) scale(${this.scale})`
      }
    },
    officePreviewUrl() {
      if (!this.resolvedUrl) return ''
      return 'https://view.officeapps.live.com/op/embed.aspx?src=' + encodeURIComponent(this.resolvedUrl)
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.loadFile()
      } else {
        this.handleClose()
      }
    },
    file() {
      if (this.visible) this.loadFile()
    }
  },
  beforeDestroy() {
    this.removeGlobalListeners()
  },
  methods: {
    loadFile() {
      this.resetImage()
      this.resolvedUrl = ''
      this.resolveSeq += 1
      if (!this.file) return
      const url = this.file.url || this.file.fileUrl || this.file.ossUrl || ''
      if (this.kind === 'pdf' || this.kind === 'office') {
        this.resolveDirectUrl(url)
        return
      }
      this.resolvedUrl = url
      this.loading = false
    },
    resolveDirectUrl(fallbackUrl) {
      const seq = this.resolveSeq
      const identifier = pickIdentifier(this.file, fallbackUrl)
      if (!identifier) {
        this.resolvedUrl = fallbackUrl
        this.loading = false
        return
      }
      this.loading = true
      // 不带 type。type=5 是视频转码，文档不要传
      axios.get(`${this.getDownloadUrl}${encodeURIComponent(identifier)}`).then((res) => {
        if (seq !== this.resolveSeq) return
        const direct = res && res.data && res.data.data && res.data.data.url
        this.resolvedUrl = direct || fallbackUrl
      }).catch(() => {
        if (seq !== this.resolveSeq) return
        this.resolvedUrl = fallbackUrl
      }).finally(() => {
        if (seq !== this.resolveSeq) return
        this.loading = false
      })
    },
    resetImage() {
      this.scale = 1
      this.translate = { x: 0, y: 0 }
    },
    onWheel(e) {
      const next = this.scale + (e.deltaY < 0 ? 0.12 : -0.12)
      this.scale = Math.min(5, Math.max(0.2, Number(next.toFixed(2))))
    },
    onDragStart(e) {
      this.dragging = true
      this.dragStart = {
        x: e.clientX,
        y: e.clientY,
        tx: this.translate.x,
        ty: this.translate.y
      }
      window.addEventListener('mousemove', this.onDragMove)
      window.addEventListener('mouseup', this.onDragEnd)
    },
    onDragMove(e) {
      if (!this.dragging) return
      this.translate = {
        x: this.dragStart.tx + e.clientX - this.dragStart.x,
        y: this.dragStart.ty + e.clientY - this.dragStart.y
      }
    },
    onDragEnd() {
      this.dragging = false
      this.removeGlobalListeners()
    },
    removeGlobalListeners() {
      window.removeEventListener('mousemove', this.onDragMove)
      window.removeEventListener('mouseup', this.onDragEnd)
    },
    onDownload() {
      if (!this.file || this.downloading) return
      const url = this.file.url || ''
      if (!url) {
        this.$message.warning('暂无下载地址')
        return
      }
      this.downloading = true
      const name = this.fileName
      fetch(url)
        .then(res => {
          if (!res.ok) throw new Error('fetch failed')
          return res.blob()
        })
        .then(blob => {
          const blobUrl = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = blobUrl
          a.download = name
          a.style.display = 'none'
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          URL.revokeObjectURL(blobUrl)
        })
        .catch(() => {
          window.open(url, '_blank')
        })
        .finally(() => {
          this.downloading = false
        })
    },
    handleClose() {
      this.resolveSeq += 1
      this.resolvedUrl = ''
      this.downloading = false
      this.loading = false
      this.resetImage()
      this.removeGlobalListeners()
    }
  }
}
</script>

<style lang="less">
.attachment-preview-dialog {
  border-radius: 12px;
  overflow: hidden;
  max-width: calc(100vw - 48px);

  .el-dialog {
    margin-bottom: 3vh;
  }

  .el-dialog__header {
    padding: 14px 18px;
    border-bottom: 1px solid #eef0f3;

    .el-dialog__title {
      font-size: 16px;
      font-weight: 600;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      display: block;
      max-width: calc(100% - 40px);
    }
  }

  .el-dialog__body {
    padding: 0;
  }

  .el-dialog__footer {
    padding: 12px 18px;
    border-top: 1px solid #eef0f3;
  }
}

.apd-body {
  position: relative;
  min-height: 560px;
  max-height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f7f8fa;
  overflow: hidden;
}

.apd-status,
.apd-fallback {
  color: #4e5969;
  text-align: center;
  font-size: 15px;
  padding: 40px;
}

.apd-footer {
  display: flex;
  align-items: center;
}

.apd-footer-spacer {
  flex: 1;
}

.apd-image-stage {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 560px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
}

.apd-image {
  max-width: 90%;
  max-height: 76vh;
  object-fit: contain;
  transform-origin: center center;
  user-select: none;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(31, 35, 41, 0.08);
  background: #fff;
}

.apd-media {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  min-height: 560px;
}

.apd-video {
  width: 100%;
  max-height: 76vh;
  outline: none;
}

.apd-audio {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 48px 24px;
}

.apd-audio-icon {
  width: 88px;
  height: 88px;
  border-radius: 50%;
  background: linear-gradient(145deg, #ffa820, #ed7064);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  color: #fff;
}

.apd-audio-player {
  width: 420px;
  max-width: 80%;
  height: 40px;
}

.apd-pdf {
  width: 100%;
  min-height: 560px;
  max-height: 80vh;
  display: flex;
}

.apd-pdf-frame {
  width: 100%;
  height: 80vh;
  border: 0;
  border-radius: 0;
}
</style>
